import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import VolumeFactorSlider from './VolumeFactorSlider';
import { CostCentre } from './quoteApi';

export function matchCostCentre(customer: string, centres: CostCentre[]): string {
  const needle = customer.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (needle.length < 3) return '';
  const hit = centres.find((centre) => {
    const code = (centre.code || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const name = (centre.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return (name && (name.includes(needle) || needle.includes(name)))
      || (code && (code.includes(needle) || needle.includes(code)));
  });
  return hit?.code || '';
}

export interface QuoteParametersValues {
  customer: string;
  costCentre: string;
  notes: string;
  enclosureH: string;
  enclosureW: string;
  enclosureD: string;
  cableType: string;
  volumeFactor: number;
  preDrilled: boolean;
  additionalIdents: boolean;
  locations: string[];
  locationDraft: string;
}

interface QuoteParametersFormProps {
  values: QuoteParametersValues;
  centres: CostCentre[];
  onChange: (patch: Partial<QuoteParametersValues>) => void;
}

const QuoteParametersForm: React.FC<QuoteParametersFormProps> = ({ values, centres, onChange }) => (
  <Box sx={{ pt: 1 }}>
    <TextField
      fullWidth
      size="small"
      label="Customer"
      value={values.customer}
      onChange={(e) => {
        const customer = e.target.value;
        const matched = matchCostCentre(customer, centres);
        onChange(matched ? { customer, costCentre: matched } : { customer });
      }}
      sx={{ mb: 1.5 }}
    />
    {centres.length > 0 ? (
      <Autocomplete
        size="small"
        sx={{ mb: 1.5 }}
        options={centres}
        value={centres.find((centre) => centre.code === values.costCentre) || null}
        onChange={(_event, centre) => onChange({ costCentre: centre?.code || '' })}
        getOptionLabel={(centre) => `${centre.code} — ${centre.name || centre.code}`}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        renderInput={(params) => (
          <TextField {...params} label="Cost centre" placeholder="Type a code or name, e.g. street" />
        )}
      />
    ) : null}
    {centres.length === 0 && (
      <TextField
        fullWidth
        size="small"
        label="Cost centre (API unavailable)"
        value={values.costCentre}
        onChange={(e) => onChange({ costCentre: e.target.value })}
        sx={{ mb: 1.5 }}
      />
    )}
    <TextField
      fullWidth
      size="small"
      label="Quote description"
      value={values.notes}
      onChange={(e) => onChange({ notes: e.target.value })}
      sx={{ mb: 1.5 }}
    />
    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
      <TextField size="small" type="number" label="H mm" value={values.enclosureH} onChange={(e) => onChange({ enclosureH: e.target.value })} sx={{ flex: 1 }} />
      <TextField size="small" type="number" label="W mm" value={values.enclosureW} onChange={(e) => onChange({ enclosureW: e.target.value })} sx={{ flex: 1 }} />
      <TextField size="small" type="number" label="D mm" value={values.enclosureD} onChange={(e) => onChange({ enclosureD: e.target.value })} sx={{ flex: 1 }} />
    </Box>
    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
      <InputLabel>Cable</InputLabel>
      <Select value={values.cableType} label="Cable" onChange={(e) => onChange({ cableType: e.target.value })}>
        <MenuItem value="Auto">Auto</MenuItem>
        <MenuItem value="Manual">Manual</MenuItem>
      </Select>
    </FormControl>
    <Typography variant="caption" color="text.secondary">Volume</Typography>
    <VolumeFactorSlider
      value={values.volumeFactor}
      onChange={(_, value) => onChange({ volumeFactor: Number(value) })}
    />
    <FormControlLabel
      control={<Switch size="small" checked={values.preDrilled} onChange={(e) => onChange({ preDrilled: e.target.checked })} />}
      label="Predrilled"
    />
    <FormControlLabel
      control={<Switch size="small" checked={values.additionalIdents} onChange={(e) => onChange({ additionalIdents: e.target.checked })} />}
      label="Extra idents"
    />
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, alignItems: 'center' }}>
      <Typography variant="caption" color="text.secondary">Locations</Typography>
      {values.locations.map((loc) => (
        <Chip key={loc} size="small" label={loc} onDelete={() => onChange({ locations: values.locations.filter((item) => item !== loc) })} />
      ))}
      <TextField
        size="small"
        placeholder="Add location"
        value={values.locationDraft}
        onChange={(e) => onChange({ locationDraft: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && values.locationDraft.trim()) {
            onChange({ locations: [...values.locations, values.locationDraft.trim()], locationDraft: '' });
          }
        }}
        sx={{ width: 140 }}
      />
    </Box>
  </Box>
);

/** Draft stays here so each keystroke does not re-render the open estimate. Labour runs when Save is pressed. */
export const QuoteParametersEditor: React.FC<{
  initial: QuoteParametersValues;
  centres: CostCentre[];
  saving?: boolean;
  onCancel: () => void;
  onSave: (values: QuoteParametersValues) => void;
}> = ({ initial, centres, saving, onCancel, onSave }) => {
  const [values, setValues] = useState(initial);
  return (
    <>
      <DialogContent>
        <QuoteParametersForm
          values={values}
          centres={centres}
          onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!values.costCentre || Boolean(saving)}
          onClick={() => onSave(values)}
        >
          Save parameters
        </Button>
      </DialogActions>
    </>
  );
};

export default QuoteParametersForm;
